import { Link } from 'react-router-dom';
import { BidText } from '../common/BidToken';
import type { ModuleDefinition, TopicDefinition } from '../../config/moduleCatalog';
import { moduleName, openingBidPrompt } from '../../config/moduleCatalog';

/**
 * One module row on a topic page (feature 023, FR-011/FR-012): module name, then
 * Practice · Master. The Study button was removed at the client's request
 * (2026-07-22) — study lives in the masthead "Study" link and the title line's
 * "Study Chapter NN" link.
 *
 * Master is INERT. Per the clarification recorded in the spec it is presented as a
 * button but does nothing and is NOT faded — the client wants the finished shape
 * visible without the greyed-out "disabled" look.
 *
 * It is therefore a `<span>`, not `<button disabled>`: a disabled button is still
 * exposed to assistive tech as a control the user might enable, whereas it will
 * never do anything in this release. Rendering it as text styled like a button
 * keeps it out of the tab order and out of the a11y tree's control list, which is
 * the honest representation. `aria-hidden` is NOT used — sighted-equivalent users
 * should still learn the step exists.
 */

export interface TopicModuleRowProps {
  module: ModuleDefinition;
  topic: TopicDefinition;
  /** Practice destination; when omitted the Practice control is inert too. */
  practiceTo?: string;
  /** Rendered instead of the Practice link — used by the 3rd-bid chooser (FR-027). */
  practiceSlot?: React.ReactNode;
}

const BTN_BASE = 'min-w-[92px] rounded-lg px-[18px] py-2.5 text-center text-[13px] font-bold';
const BTN_INERT = `${BTN_BASE} border border-line-strong bg-cream text-ink`;

// Practice and Master travel as ONE group. On a phone in portrait the module name plus
// both buttons don't fit one line, and as loose row children only Master wrapped — it
// dropped to the left edge under a right-hand Practice, which read as two unrelated
// controls. `phone-pt:w-full` moves the pair together onto the second line, still
// right-aligned so Practice keeps the edge it has everywhere else. The inner gap-3.5
// repeats what the row gave them as direct children, so nothing moves on tablet and up.
const CONTROLS = 'flex items-center gap-3.5 phone-pt:w-full phone-pt:justify-end';

export function TopicModuleRow({ module, topic, practiceTo, practiceSlot }: TopicModuleRowProps) {
  // A redirect module with no variants (feature 033's "Jeff's problems") has nothing to
  // choose, so Practice is an ordinary link to the page it reuses — same styling, same test
  // id as a pool module's link. A redirect WITH variants is the caller's chooser slot.
  const plainRedirect =
    !practiceSlot && !practiceTo && module.redirect && !module.redirect.variants?.length
      ? module.redirect.route
      : undefined;
  const linkTo = practiceTo ?? plainRedirect;

  return (
    <div
      // `relative` is the anchor the 3rd-bid chooser's panel falls back to on a phone —
      // see the comment in ThirdBidChooser.
      className="relative mb-2.5 flex flex-wrap items-center gap-3.5 rounded-[10px] border border-line bg-card px-5 py-4"
      data-testid={`module-row-${module.slug}`}
    >
      <span className="min-w-[200px] flex-1 text-[15.5px] font-bold text-ink">
        {/* The yes/no module's row asks its own question — "Should you open 1♣?" — in place of the
            bare name "Opening bid" (client request, 2026-09-18). This row only: the module is still
            NAMED "Opening bid" everywhere its name is used (practice masthead, header trail, Ask
            Jeff section), and the wording is the practice page's own prompt, from the same source. */}
        <BidText
          text={module.format === 'yes-no' ? openingBidPrompt(topic) : moduleName(module, topic)}
        />
      </span>

      {/* A div, not a span: the 3rd-bid chooser slot is a positioned block. */}
      <div className={CONTROLS}>
        {practiceSlot ??
          (linkTo ? (
            <Link
              to={linkTo}
              className={`${BTN_BASE} bg-primary-600 text-white no-underline transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2`}
              data-testid={`module-practice-${module.slug}`}
            >
              Practice
            </Link>
          ) : (
            <span className={BTN_INERT} data-testid={`module-practice-${module.slug}`}>
              Practice
            </span>
          ))}

        <span
          className={`${BTN_BASE} bg-[#8c1d1d] text-white`}
          data-testid={`module-master-${module.slug}`}
        >
          Master
        </span>
      </div>
    </div>
  );
}
